// frontend/src/components/Navbar.jsx
// ======================================================
// ✅ Navbar.jsx – Barra superior institucional Evalúa Salud IPS
// Autor: Breyner Javier Montalvo Peñarredonda
// ------------------------------------------------------
// Diseño moderno, profesional y accesible. Incluye:
// - Integración con Sidebar
// - Alternancia de tema (claro / oscuro)
// - Identidad corporativa coherente
// ======================================================

import PropTypes from "prop-types";
import { memo, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import "../styles/Navbar.css";
import BotonTema from "./BotonTema";

const Navbar = memo(function Navbar({ onToggleSidebar }) {
  const { usuario } = useAuth();

  // ======================================================
  // 👤 Nombre visible del usuario autenticado
  // ======================================================
  const nombreVisible = useMemo(() => {
    if (!usuario) {return "";}
    return usuario.nombre?.trim() || usuario.email || "Usuario";
  }, [usuario]);

  // ======================================================
  // 🏷 Rol formateado
  // ======================================================
  const rolFormateado = useMemo(
    () => usuario?.rol?.toUpperCase() || "USUARIO",
    [usuario]
  );

  // ======================================================
  // 🧱 Render principal
  // ======================================================
  return (
    <header className="navbar" role="banner" aria-label="Barra principal">
      {/* ---------- BOTÓN SIDEBAR ---------- */}
      <button
        type="button"
        className="navbar__toggle"
        aria-label="Abrir o colapsar menú lateral"
        aria-controls="sidebar"
        aria-expanded="false"
        onClick={onToggleSidebar}
      >
        <span className="navbar__icon" aria-hidden="true">
          ☰
        </span>
      </button>

      {/* ---------- IDENTIDAD INSTITUCIONAL ---------- */}
      <div
        className="navbar__brand"
        role="group"
        aria-label="Identidad institucional Evalúa Salud IPS"
      >
        <img
          src="/logo-evalua.png"
          alt="Logo Evalúa Salud IPS"
          className="navbar__logo"
          loading="lazy"
          decoding="async"
          draggable="false"
          onError={(e) => {
            e.currentTarget.src = "/logo192.png";
          }}
        />
        <div className="navbar__titles">
          <h1 className="navbar__title">Evalúa Salud IPS</h1>
          <p className="navbar__subtitle">Plataforma Administrativa</p>
        </div>
      </div>

      {/* ---------- BLOQUE DE USUARIO AUTENTICADO ---------- */}
      {usuario ? (
        <nav
          className="navbar__user"
          aria-label="Opciones del usuario autenticado"
        >
          <div className="navbar__info">
            <span
              className="navbar__name"
              title={nombreVisible}
              aria-label={`Usuario: ${nombreVisible}`}
            >
              {nombreVisible}
            </span>
            <span
              className="navbar__role"
              aria-label={`Rol: ${rolFormateado}`}
            >
              {rolFormateado}
            </span>
          </div>

          {/* ---------- BOTÓN DE TEMA (CLARO / OSCURO) ---------- */}
          <BotonTema />
        </nav>
      ) : (
        <div className="navbar__guest" aria-label="Usuario invitado">
          <span>Invitado</span>
        </div>
      )}
    </header>
  );
});

Navbar.displayName = "Navbar";

Navbar.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired
};

export default Navbar;
